import { Case, Specimen, CaseSlide } from "pwapi";

// TODO: This is an example of an OpenAPI opportunity
import { CaseDiagnosis } from "custom-models";

import {
  CANCEL_EDIT_SCREEN_DIAGNOSES_SUCCESS,
  CANCEL_MODIFY_REPORT_SUCCESS,
  CASE_DETAIL_SUCCESS,
  CASE_DIAGNOSES_SUCCESS,
  CASE_REPORT_SUCCESS,
  CASE_RECALL_REPORT_SUCCESS,
  UPDATE_CASE_SPECIMEN_SUCCESS,
  DIAGNOSE_SUCCESS,
  EDIT_SCREEN_DIAGNOSES_SUCCESS,
  MODIFY_REPORT_SUCCESS,
  OPTIMISTICALLY_EDIT_DIAGNOSIS,
  SET_MACRO_CODE,
  UPDATE_DIAGNOSIS_SUCCESS,
  DELETE_DIAGNOSIS_SUCCESS,
} from "../actions/constants";

export interface SpecimenDetail extends Specimen {
  slides?: CaseSlide[];
  diagnoses: CaseDiagnosis[];
  ai_diagnosis?: CaseDiagnosis[];
  loading?: boolean;
  inputMacroCode?: string;
}

export const filterDiagnosisBasedOnStatus = (
  d: CaseDiagnosis,
  status: Case.status
) => {
  return status === Case.status.ACTIVE || status === Case.status.WORKING
    ? d.source === CaseDiagnosis.source.SCREEN ||
        (d.source === CaseDiagnosis.source.FINALIZE && d.is_completed === false)
    : status === Case.status.LIS_PENDING
    ? (d.source === CaseDiagnosis.source.FINALIZE ||
        d.source === CaseDiagnosis.source.ADDEND ||
        d.source === CaseDiagnosis.source.AMEND ||
        d.source === CaseDiagnosis.source.CORRECTION) &&
      d.is_completed === true
    : status === Case.status.FINALIZED
    ? ((d.source === CaseDiagnosis.source.FINALIZE ||
        d.source === CaseDiagnosis.source.PRELIMINARY ||
        d.source === CaseDiagnosis.source.SCREEN) &&
        d.is_completed === true) ||
      d.source === CaseDiagnosis.source.ADDEND ||
      d.source === CaseDiagnosis.source.AMEND ||
      d.source === CaseDiagnosis.source.CORRECTION
    : false;
};

export const mapSlidesToSpecimen = (
  specimen: Specimen[],
  slides: CaseSlide[],
  diagnoses: CaseDiagnosis[],
  status: Case.status
): SpecimenDetail[] => {
  return specimen
    .map((s) => {
      return {
        ...s,
        slides: slides.filter((sl) => sl.specimen_id === s.id),
        diagnoses:
          diagnoses?.filter(
            (d) =>
              d.specimen === s.id && filterDiagnosisBasedOnStatus(d, status)
          ) || [],
        ai_diagnosis:
          diagnoses?.filter(
            (d: CaseDiagnosis) =>
              d.specimen === s.id && d.source === CaseDiagnosis.source.AI
          ) || [],
      };
    })
    .sort((a, b) =>
      a.name.localeCompare(b.name, "en", {
        numeric: true,
        sensitivity: "base",
      })
    );
};

const reducer = (state: SpecimenDetail[] = [], action: any) => {
  switch (action.type) {
    case EDIT_SCREEN_DIAGNOSES_SUCCESS:
    case CANCEL_EDIT_SCREEN_DIAGNOSES_SUCCESS:
    case MODIFY_REPORT_SUCCESS:
    case CANCEL_MODIFY_REPORT_SUCCESS:
    case CASE_RECALL_REPORT_SUCCESS:
    case CASE_DETAIL_SUCCESS:
      const { specimens, slides, case_diagnosis, status } = action.payload;
      if (specimens)
        return mapSlidesToSpecimen(specimens, slides, case_diagnosis, status);
      return state;
    case CASE_REPORT_SUCCESS:
      const serialization = action.payload.response.serialization;
      const {
        specimens: serSpecimens,
        slides: serSlides,
        case_diagnosis: serDiagnosis,
      } = serialization;
      if (serSpecimens)
        return mapSlidesToSpecimen(
          serSpecimens,
          serSlides,
          serDiagnosis,
          Case.status.FINALIZED
        );
      return state;
    case CASE_DIAGNOSES_SUCCESS:
      return state.map((s) => ({
        ...s,
        diagnoses: action.payload.ai_only
          ? s.diagnoses
          : action.payload.diagnoses.filter(
              (d: CaseDiagnosis) =>
                d.specimen === s.id &&
                filterDiagnosisBasedOnStatus(d, action.payload.status)
            ),
        // AI diagnosis only come from this full success
        ai_diagnosis: action.payload.diagnoses.filter(
          (d: CaseDiagnosis) =>
            d.specimen === s.id && d.source === CaseDiagnosis.source.AI
        ), // TODO: could be just the first here... assuming we only have one
      }));
    case DIAGNOSE_SUCCESS:
      return state.map((s) => {
        if (s.id === action.payload.caseDiagnosis.specimen) {
          return {
            ...s,
            loading: false,
            diagnoses: [...s.diagnoses, action.payload.caseDiagnosis],
          };
        }
        return s;
      });
    case OPTIMISTICALLY_EDIT_DIAGNOSIS:
      return state.map((s) => {
        if (s.id === action.payload.specimen.id) {
          return {
            ...s,
            loading: true,
            diagnoses: s.diagnoses.map((d) => {
              if (d.id === action.payload.diagnosis.id) {
                return {
                  ...d,
                  ...action.payload.diagnosis,
                };
              }
              return d;
            }),
          };
        }
        return s;
      });
    case UPDATE_DIAGNOSIS_SUCCESS:
      const caseDiagnosis: CaseDiagnosis = action.payload.caseDiagnosis;
      return state.map((s) => {
        if (s.id === caseDiagnosis.specimen) {
          return {
            ...s,
            loading: false,
            diagnoses: s.diagnoses.map((d) => {
              if (d.id === caseDiagnosis.id) {
                return {
                  ...d,
                  // Modify only CPT codes, which drift from user input
                  cpt_codes: caseDiagnosis.cpt_codes,
                };
              }
              return d;
            }),
          };
        }
        return s;
      });
    case DELETE_DIAGNOSIS_SUCCESS:
      return state.map((s) => {
        return {
          ...s,
          loading: false,
          diagnoses: s.diagnoses.filter((d) => d.id !== action.args.id),
        };
      });
    case SET_MACRO_CODE:
      return state.map((s) => {
        if (s.id === action.payload.id) {
          return {
            ...s,
            inputMacroCode: action.payload.inputMacroCode,
          };
        }
        return s;
      });
    case UPDATE_CASE_SPECIMEN_SUCCESS:
      return state
        ? state.map((s) => {
            return s.id === action.payload.response.id
              ? {
                  ...s,
                  ...action.payload.response,
                }
              : s;
          })
        : state;
    default:
      return state;
  }
};

export default reducer;
